import { ExistingScribeModel } from 'js/types';

export const GET_SCRIBE_FOR_PLAYBACK_ERROR = 'GET_SCRIBE_FOR_PLAYBACK_ERROR';
export const GET_SCRIBE_FOR_PLAYBACK_SUCCESS = 'GET_SCRIBE_FOR_PLAYBACK_SUCCESS';
export const GET_SCRIBE_FOR_RENDER = 'GET_SCRIBE_FOR_RENDER';
export const PLAY_FROM_EDITOR = 'PLAY_FROM_EDITOR';
export const PLAYBACK_IS_READY = 'PLAYBACK_IS_READY';
export const START_PLAYBACK = 'START_PLAYBACK';
export const STOP_PLAYBACK = 'STOP_PLAYBACK';

export interface GetScribeForPlaybackErrorAction {
  type: typeof GET_SCRIBE_FOR_PLAYBACK_ERROR;
  error: string;
}

export const getScribeForPlaybackError = (error: string) => ({
  type: GET_SCRIBE_FOR_PLAYBACK_ERROR,
  error
});

export interface GetScribeForPlaybackSuccessAction {
  type: typeof GET_SCRIBE_FOR_PLAYBACK_SUCCESS;
  scribeData: ExistingScribeModel;
}

export const getScribeForPlaybackSuccess = (scribeData: ExistingScribeModel): GetScribeForPlaybackSuccessAction => ({
  type: GET_SCRIBE_FOR_PLAYBACK_SUCCESS,
  scribeData
});

export interface GetScribeForRenderAction {
  type: typeof GET_SCRIBE_FOR_RENDER;
  scribeId: number;
  startTimeMs: number;
  durationMs: number;
}

export const getScribeForRender = ({
  scribeId,
  startTimeMs,
  durationMs
}: Omit<GetScribeForRenderAction, 'type'>): GetScribeForRenderAction => ({
  type: GET_SCRIBE_FOR_RENDER,
  scribeId,
  startTimeMs,
  durationMs
});

export type PlaybackStartFrom = 'project' | 'element' | 'scene' | 'time';

interface PlayFromEditorActionBase {
  type: typeof PLAY_FROM_EDITOR;
  scribeId: number;
  autoPlay: boolean;
  isRecordingAudio: boolean;
  previewAudioPlayback: boolean;
  audioUrl: string | null;
  playAudio: boolean;
}

export interface PlayFromEditorElementAction extends PlayFromEditorActionBase {
  startFrom: 'element';
  startingElementId: string | null;
}

export interface PlayFromEditorProjectAction extends PlayFromEditorActionBase {
  startFrom: 'project';
}

export interface PlayFromEditorSceneAction extends PlayFromEditorActionBase {
  startFrom: 'scene';
  startingSceneIndex: number | null;
}

export interface PlayFromEditorTimeAction extends PlayFromEditorActionBase {
  startFrom: 'time';
  startingTime: number | null;
}

type PlayFromEditorActionInputBase = {
  scribeId: number;
  autoPlay?: boolean;
  isRecordingAudio?: boolean;
  previewAudioPlayback?: boolean;
  audioUrl?: string | null;
  playAudio?: boolean;
};

export type PlayFromEditorActionInput =
  | (PlayFromEditorActionInputBase & {
      startFrom: 'project';
    })
  | (PlayFromEditorActionInputBase & {
      startFrom: 'element';
      startingElementId?: string | null;
    })
  | (PlayFromEditorActionInputBase & {
      startFrom: 'scene';
      startingSceneIndex?: number | null;
    })
  | (PlayFromEditorActionInputBase & {
      startFrom: 'time';
      startingTime?: number | null;
    });

export type PlayFromEditorAction =
  | PlayFromEditorProjectAction
  | PlayFromEditorElementAction
  | PlayFromEditorSceneAction
  | PlayFromEditorTimeAction;

export const playFromEditor = (input: PlayFromEditorActionInput): PlayFromEditorAction => {
  const baseAction: PlayFromEditorActionBase = {
    type: PLAY_FROM_EDITOR,
    scribeId: input.scribeId,
    autoPlay: input.autoPlay ?? true,
    isRecordingAudio: input.isRecordingAudio ?? false,
    previewAudioPlayback: input.previewAudioPlayback ?? false,
    audioUrl: input.audioUrl ?? null,
    playAudio: input.playAudio ?? true
  };

  if (input.startFrom === 'scene') {
    return {
      ...baseAction,
      startFrom: 'scene',
      startingSceneIndex: input.startingSceneIndex ?? null
    };
  }

  if (input.startFrom === 'project') {
    return {
      ...baseAction,
      startFrom: 'project'
    };
  }

  if (input.startFrom === 'time') {
    return {
      ...baseAction,
      startFrom: 'time',
      startingTime: input.startingTime ?? null
    };
  }

  return {
    ...baseAction,
    startFrom: 'element',
    startingElementId: input.startingElementId ?? null
  };
};

export const playbackIsReady = () => ({
  type: PLAYBACK_IS_READY
});

export const startPlayback = () => ({
  type: START_PLAYBACK
});

export const stopPlayback = () => ({
  type: STOP_PLAYBACK
});
